/**
 * Validated DICOM identity keys. Each is branded, so a raw DICOM string
 * cannot be used where a checked key is required, and each is made only
 * through its factory.
 */

/**
 * Validated frame-of-reference key.
 *
 * `UNKNOWN_FOR` is a distinct value that matches nothing, including another
 * unknown, so an absent, empty or blank frame of reference never implies
 * shared geometry. Construct only via `toFrameOfReferenceUid`.
 */
export type FrameOfReferenceUid = string & {
    readonly __brand: 'FrameOfReferenceUid';
};

const UNKNOWN_FOR_SENTINEL = '__unknown_frame_of_reference__';

/** The frame of reference that matches nothing. */
export const UNKNOWN_FOR = UNKNOWN_FOR_SENTINEL as FrameOfReferenceUid;

/**
 * Validating factory. Empty, whitespace-only, null or undefined input
 * collapses to `UNKNOWN_FOR`. A present value is trimmed and branded.
 */
export function toFrameOfReferenceUid(
    raw: string | undefined | null
): FrameOfReferenceUid {
    if (raw == null) {
        return UNKNOWN_FOR;
    }
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return UNKNOWN_FOR;
    }
    return trimmed as FrameOfReferenceUid;
}

/** True when the frame of reference is a real, comparable key. */
export function isKnownFor(forUid: FrameOfReferenceUid): boolean {
    return forUid !== UNKNOWN_FOR;
}

/**
 * Whether two raw frame-of-reference UIDs name one known frame: geometry in
 * one is geometry in the other with no registration. A missing or blank
 * frame shares nothing, not even with another missing one.
 */
export function sharesFrameOfReference(
    a: string | undefined | null,
    b: string | undefined | null
): boolean {
    const known = toFrameOfReferenceUid(a);
    return isKnownFor(known) && known === toFrameOfReferenceUid(b);
}

/**
 * Why no registration can map one raw frame-of-reference UID onto the
 * other: `'frameUnknown'` when either is missing or blank, `'sameFrame'`
 * when both name one frame, which needs no registration. Undefined when a
 * registration between the two can be made.
 */
export function frameRegistrationRefusal(
    a: string | undefined | null,
    b: string | undefined | null
): 'frameUnknown' | 'sameFrame' | undefined {
    const first = toFrameOfReferenceUid(a);
    const second = toFrameOfReferenceUid(b);
    if (!isKnownFor(first) || !isKnownFor(second)) {
        return 'frameUnknown';
    }
    return first === second ? 'sameFrame' : undefined;
}

/**
 * Validated series-instance-UID key: one series, whatever the padding of
 * the UID that names it. The dataset registry keys an image's one shared
 * `Environment` by it, so a raw string never mints a second entry for one
 * series. Construct only via {@link toSeriesInstanceUid}.
 */
export type SeriesInstanceUid = string & {
    readonly __seriesInstanceUidBrand: unique symbol;
};

/** Trims and brands a series UID. Throws on a missing or blank UID. */
export function toSeriesInstanceUid(raw: string): SeriesInstanceUid {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        throw new Error('SeriesInstanceUid must be a non-empty UID.');
    }
    return trimmed as SeriesInstanceUid;
}

/**
 * The series key of a UID that may be missing: undefined for a missing or
 * blank UID, else {@link toSeriesInstanceUid}.
 */
export function seriesInstanceUidOf(
    raw: string | undefined | null
): SeriesInstanceUid | undefined {
    return raw == null || raw.trim().length === 0
        ? undefined
        : toSeriesInstanceUid(raw);
}
